import * as yup from 'yup'
import {
    carrierTextMax,
    carrierTextMin,
    emailMax,
    nameMax,
    nameMin,
} from '@/src/modules/build-a-card/constants'
import postLanguageCheck from '@/src/common/queries/PostLanguageCheckQuery'
import {
    PHONE_REG_EXP,
    PHONE_REG_WITH_AREA_ONLY_NUMBERS,
} from '@/src/modules/pg-plus/constants'
import {
    maxMessageChars,
    maxSingleCardLoad,
    minCardLoad,
    minMessageChars,
} from '@/src/modules/checkout/strings'
import {
    checkIfPhoneIsNotValid,
    checkIfPhoneNotBlocked,
} from './validationHelpers'

const Step1SchemaGeneral = yup.object({
    designImage: yup
        .string()
        .label('Design')
        .required('Please upload a photo or select a pre-designed image')
        .url(),
    recipientMessage: yup
        .string()
        .label('Message')
        .required()
        .min(
            minMessageChars,
            `Message must contain a minimum of ${minMessageChars} characters`
        )
        .max(
            maxMessageChars,
            `Message cannot contain more than ${maxMessageChars} characters`
        )
        .test(
            'badWords',
            'Contains words or phrases that are not permitted',
            postLanguageCheck
        ),
    senderName: yup
        .string()
        .label('From')
        .required()
        .max(
            nameMax,
            `From field cannot contain more than ${nameMax} characters`
        )
        .test(
            'badWords',
            'Contains words or phrases that are not permitted',
            postLanguageCheck
        ),
})

export function buildMinMaxDenomSchema(minDenom: number, maxDenom: number) {
    return {
        denomination: yup
            .string()
            .label('Amount')
            .required()
            .test(
                'denomValueLegit',
                `Amount must be between $${minDenom} and $${maxDenom}`,
                (value: string | undefined) => {
                    if (!value) return false

                    // if the gift value is above or below the min/max
                    // show an error
                    const floatVal = parseFloat(value?.replace(/[^0-9.]+/g, ''))
                    return minDenom <= floatVal && floatVal <= maxDenom
                }
            ),
    }
}

export function buildFixedDenomSchema(fixedDenoms: number[]) {
    // create a string that lists valid amounts for error message
    let fixedDenomString: string = 'Amount must be '

    fixedDenoms?.forEach((denom, index) => {
        const isLastItem: boolean = index === fixedDenoms.length - 1

        if (isLastItem) {
            const textToAdd: string = index === 0 ? `$${denom}` : `or $${denom}`
            return (fixedDenomString += textToAdd)
        }

        fixedDenomString += `$${denom}, `
    })

    return {
        denomination: yup
            .string()
            .label('Amount')
            .required()
            .test(
                'denomValueLegit',
                fixedDenomString,
                (value: string | undefined) => {
                    if (!value) return false

                    // if the gift value does not match fixed values, return error
                    const floatVal = parseFloat(value?.replace(/[^0-9.]+/g, ''))
                    return fixedDenoms?.includes(floatVal)
                }
            ),
    }
}

export const PGPlusStep1SchemaWithFixedDenoms = (
    minDenom: number,
    maxDenom: number,
    fixedDenoms: number[] | undefined
) => {
    // as is, fixed denoms are only used in partner pages
    // and if brand only allows fixed egift values
    if (Array.isArray(fixedDenoms) && fixedDenoms?.length > 0) {
        const fixedSchema = buildFixedDenomSchema(fixedDenoms)
        return Step1SchemaGeneral.shape(fixedSchema)
    }

    const minMaxSchema = buildMinMaxDenomSchema(minDenom, maxDenom)
    return Step1SchemaGeneral.shape(minMaxSchema)
}

export const VirtualStep1Schema = Step1SchemaGeneral.shape({
    denomination: yup
        .string()
        .label('Amount')
        .required()
        .test(
            'denomValueLegit',
            `Amount must be between $${minCardLoad} and $${maxSingleCardLoad}`,
            (value: string | undefined) => {
                if (!value) return false
                const floatVal = parseFloat(value?.replace(/[^0-9.]+/g, ''))
                return minCardLoad <= floatVal && floatVal <= maxSingleCardLoad //@FUTURE: Dynamic data from server side.
            }
        ),
})

export const stepGreetingCardSchema = yup.object({
    carrierType: yup.string().required('Please choose a carrier type'),
})

export const step2schema = (hasGreetingCard = false) => {
    return yup.object({
        recipientName: yup
            .string()
            .label('To')
            .when([], {
                is: () => !hasGreetingCard,
                then: yup
                    .string()
                    .required()
                    .min(
                        nameMin,
                        `Name must contain at least ${nameMin} characters`
                    )
                    .max(
                        nameMax,
                        `Name cannot contain more than ${nameMax} characters`
                    )
                    .test(
                        'badWords',
                        'Contains words or phrases that are not permitted',
                        postLanguageCheck
                    ),
            }),
        senderName: yup
            .string()
            .label('From')
            .when([], {
                is: () => !hasGreetingCard,
                then: yup
                    .string()
                    .required()
                    .min(
                        nameMin,
                        `Name must contain at least ${nameMin} characters`
                    )
                    .max(
                        nameMax,
                        `Name cannot contain more than ${nameMax} characters`
                    )
                    .test(
                        'badWords',
                        'Contains words or phrases that are not permitted',
                        postLanguageCheck
                    ),
            }),
        recipientMessage: yup
            .string()
            .label('Message')
            .required()
            .min(
                carrierTextMin,
                `Message must contain at least ${carrierTextMin} characters`
            )
            .max(
                carrierTextMax,
                `Message cannot contain more than ${carrierTextMax} characters`
            )
            .test(
                'badWords',
                'Contains words or phrases that are not permitted',
                postLanguageCheck
            ),
    })
}

export const singleRecipientEmailSchema = yup.object({
    recipientEmail: yup
        .string()
        .trim()
        .label('Email address')
        .required()
        .max(emailMax, `Email cannot contain more than ${emailMax} characters`)
        .email(),
})

export const singleRecipientPhoneSchema = yup.object({
    recipientPhone: yup
        .string()
        .required()
        .label('Phone number')
        .matches(PHONE_REG_EXP, 'A valid phone number is required')
        .test(
            'data.isBlocked',
            "This number has unsubscribed from our text messages and is unable to have a gift sent via text until sending 'START' to 71689. Until then, you can send this gift to an email address, should you choose",
            async (value) => {
                const formattedPhone = String(value).replace(/\D/g, '')
                if (value && formattedPhone.length === 10) {
                    return await checkIfPhoneNotBlocked({
                        phone: value,
                        isBuyer: false,
                    })
                }
                return false
            }
        )
        .test(
            'isNotValid',
            'We are unable to send a text to this number.  Landline and international numbers are not supported. Please enter a valid phone number.',
            async (value) => {
                const formattedPhone = String(value).replace(/\D/g, '')
                if (value && formattedPhone.length === 10) {
                    return await checkIfPhoneIsNotValid({
                        phone: value,
                        isBuyer: false,
                    })
                }
                return false
            }
        ),
})

export const singleRecipientNameSchema = yup.object({
    recipientName: yup
        .string()
        .label('Name')
        .required()
        .max(nameMax, `Name cannot contain more than ${nameMax} characters`)
        .test(
            'badWords',
            'Contains words or phrases that are not permitted',
            postLanguageCheck
        ),
})

export const recipientEmailSchema = yup.object({
    deliveryMethod: yup.string().required().label('Delivery method'),
    recipientName: yup
        .string()
        .label('Name')
        .required()
        .max(nameMax, `Name cannot contain more than ${nameMax} characters`)
        .test(
            'badWords',
            'Contains words or phrases that are not permitted',
            postLanguageCheck
        ),
    recipientEmail: yup
        .string()
        .trim()
        .label('Email address')
        .required()
        .max(emailMax, `Email cannot contain more than ${emailMax} characters`)
        .email(),
})

export const recipientPhoneSchema = yup.object({
    deliveryMethod: yup.string().required().label('Delivery method'),
    recipientName: yup
        .string()
        .label('Name')
        .required()
        .max(nameMax, `Name cannot contain more than ${nameMax} characters`)
        .test(
            'badWords',
            'Contains words or phrases that are not permitted',
            postLanguageCheck
        ),
    recipientPhone: yup
        .string()
        .required()
        .label('Phone number')
        .matches(PHONE_REG_EXP, 'A valid phone number is required')
        .test(
            'data.isBlocked',
            "This number has unsubscribed from our text messages and is unable to have a gift sent via text until sending 'START' to 71689. Until then, you can send this gift to an email address, should you choose",
            async (value) => {
                const formattedPhone = String(value).replace(/\D/g, '')
                if (value && formattedPhone.length === 10) {
                    return await checkIfPhoneNotBlocked({
                        phone: value,
                        isBuyer: false,
                    })
                }
                return false
            }
        )
        .test(
            'isNotValid',
            'We are unable to send a text to this number.  Landline and international numbers are not supported. Please enter a valid phone number.',
            async (value) => {
                const formattedPhone = String(value).replace(/\D/g, '')
                if (value && formattedPhone.length === 10) {
                    return await checkIfPhoneIsNotValid({
                        phone: value,
                        isBuyer: false,
                    })
                }
                return false
            }
        ),
})

export const isValidPhoneFormatOnlyNumbersAndPlus = yup.object({
    phone: yup
        .string()
        .required()
        .label('Phone number')
        .matches(
            PHONE_REG_WITH_AREA_ONLY_NUMBERS,
            'A valid phone number is required'
        ),
})
